import axios from "axios";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

// Icons and Global components
import { LogIn, RefreshCw } from "feather-icons-react/build/IconComponents";
import ResponsiveTable from "components/ResponsiveTable";
import { IconedButton } from "components/IconedButton";
import { FieldInput } from "components/fieldInput";
import { Animate, ViewContext } from "components/lib";

// Helpers
import StringUtils from "../../helpers/stringUtils";
import DateUtils from "../../helpers/dateUtils";
import { formatCurrency } from "helpers/currencyUtil";
import { getLatestSubmissionNumber } from 'helpers/submission';

// Table
import { policyColumns } from "./columns";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { CreatePolicyModal } from '../../components/modals/CreatePolicyModal';

const stringUtils = new StringUtils();
const dateUtils = new DateUtils();

export function Bound() {
  const context = useContext(ViewContext);
  const cancelTokenRef = useRef(null);
  const debounceTimeout = useRef(null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const [searchQueryString] = useState("");
  const [agencyFilter, setAgencyFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [agenciesOptions, setAgenciesOptions] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [showCreatePolicyModal, setShowCreatePolicyModal] = useState(false);

  const debounceSearch = useCallback((searchValue) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedFilter(searchValue);

      if (searchValue) {
        setPagination(prev => ({
          ...prev,
          pageIndex: 0
        }));
      }
    }, 400);
  }, []);

  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value);
    debounceSearch(value);
  }

  async function fetchFilterOptions() {
    try {
      const [agenciesResponse, companiesResponse] = await Promise.all([
        axios.get('/api/account/all/minimal'),
        axios.get('/api/company/all')
      ]);

      setAgenciesOptions(
        agenciesResponse.data.data.map((agency) => {
          return {
            label: agency.name,
            value: agency.id,
          };
        })
      );

      setCompaniesOptions(
        companiesResponse.data.data.map((company) => {
          return {
            label: stringUtils.capitalizeFirstLetter(company.name).replace("Llc", "LLC"),
            value: company.id,
          };
        })
      );
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
      context.notification.show(
        'Failed to load filter options',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  }

  async function listPolicies(page, search, companyFilter, agencyFilter, statusFilter) {
    setLoading(true);

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Operation canceled due to new request.');
    }

    cancelTokenRef.current = axios.CancelToken.source();

    try {
      const response = await axios.get(
        `/api/policies/admin`,
        {
          params: {
            page: page,
            limit: pagination.pageSize,
            search: search || undefined,
            companyFilter: companyFilter || undefined,
            agencyFilter: agencyFilter || undefined,
            statusFilter: statusFilter || undefined
          },
          cancelToken: cancelTokenRef.current.token
        }
      );

      setLoading(false);

      const policiesData = response.data.data.data.policies.map((value) => {
        return {
          id: value.id,
          company_id: value.company_id,
          account_id: value.account_id,
          submission_number: getLatestSubmissionNumber(value),
          Company: stringUtils
            .capitalizeFirstLetter(value.company_name)
            .replaceAll("Llc", "LLC"),
          "Agency Name": value.account_name || "N/A",
          carrier: Array.isArray(value.carrier)
            ? value.carrier.join(', ')
            : (value.carrier || 'N/A'),
          "Premium Amount": formatCurrency(value.premium || 0),
          effective_date: value.effective_date,
          "Date submitted": dateUtils.timestamp(value.createdAt),
          status: value.status === "bound" ? "active" : value.status.replaceAll("_", " "),
          quote: value,
        };
      });

      setData(policiesData);
      setPagination({
        ...pagination,
        totalItems: response.data.data.rows,
        totalPages: response.data.data.pages_count,
      });
    } catch (error) {
      if (!axios.isCancel(error)) {
        setLoading(false);
        console.error(error);
        context.notification.show("Failed to load table data", "error", true, "toast", "x-circle");
      }
    }
  }

  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listPolicies(
      pagination.pageIndex + 1,
      debouncedFilter,
      companyFilter,
      agencyFilter,
      statusFilter
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, debouncedFilter, companyFilter, agencyFilter, statusFilter]);

  const handleEditPolicy = (row) => {
    window.location.href = '/admin/policy/' + row.id;
  };


  const updateQuote = async (submissionNumber) => {
    try {
      const currentItem = data.find(item => item.submission_number === submissionNumber);
      console.log(`Before update - Submission: ${submissionNumber}, Current Status: ${currentItem?.status}`);

      const response = await axios
        .patch(`/api/policy/update/status/${submissionNumber}`)

      const newStatus = response.data.data.status === "bound" ? "active" : response.data.data.status.replaceAll("_", " ");
      console.log(`After API call - New Status from CoverWhale: ${response.data.data.status}, Display Status: ${newStatus}`);

      // Update the local data state immediately with new status
      setData(prevData =>
        prevData.map(item =>
          item.submission_number === submissionNumber
            ? {
              ...item,
              status: newStatus
            }
            : item
        )
      );

      return context.notification.show(
        'Quote synced successfully',
        'success',
        true,
        'toast',
        'check-circle'
      );
    } catch (error) {
      return context.notification.show(
        error.response.data.message,
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  }

  // Add action column to columns
  const columnsWithActions = [
    ...policyColumns,
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEditPolicy(row.original)}
            className="p-1 rounded hover:bg-gray-200"
            title="Edit policy"
          >
            <LogIn size={16} color="#637381" />
          </button>
          <button
            onClick={() => updateQuote(row.original.submission_number)}
            className="p-1 rounded hover:bg-gray-200"
            title="Refresh policy"
          >
            <RefreshCw size={16} color="#637381" />
          </button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns: columnsWithActions,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      rowSelection,
      pagination,
    },
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onSortingChange: setSorting,
    onGlobalFilterChange: handleGlobalFilterChange,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Filter components to pass to ResponsiveTable
  const filterComponents = (
    <div className="p-4 space-y-4">
      <div>
        <FieldInput
          id="agency_filter"
          type="multi_select"
          label="Filter by agency"
          isDisabled={loading}
          value={selectedAgencies}
          handleChange={(agency) => {
            const agencyValues = agency.map(a => a.value);
            setAgencyFilter(agencyValues);
            setSelectedAgencies(agency);
          }}
          options={agenciesOptions}
        />
      </div>
      <div>
        <FieldInput
          id="company_filter"
          type="multi_select"
          label="Filter by company"
          isDisabled={loading}
          value={selectedCompanies}
          handleChange={(company) => {
            const companyValues = company.map(c => c.value);
            setCompanyFilter(companyValues);
            setSelectedCompanies(company);
          }}
          options={companiesOptions}
        />
      </div>
      <div>
        <FieldInput
          id="status_filter"
          type="multi_select"
          label="Filter by status"
          isDisabled={loading}
          defaultValue={[]}
          handleChange={(status) => {
            setStatusFilter(
              status.map((s) => s.value)
            );
          }}
          options={[
            { value: "bound", label: "Active" },
            { value: "rejected", label: "Rejected" },
            { value: "canceled", label: "Canceled" },
            { value: "pending_cancelation", label: "Pending Cancellation" },
          ]}
        />
      </div>
      <div className="flex justify-center">
        <button
          className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-lg"
          onClick={() => {
            setPagination(prev => ({ ...prev, pageIndex: 0 }));
            listPolicies(1, debouncedFilter, companyFilter.join(','), agencyFilter.join(','), statusFilter.join(','));
          }}
        >
          Apply Filters
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Animate type="pop">
        <div className="flex items-center justify-end mb-4 space-x-4">
          <IconedButton
            size="160px"
            color="#d32425"
            textColor="white"
            icon="plus-circle"
            text="Create Policy"
            marginRight
            action={() => setShowCreatePolicyModal(true)}
          />
        </div>

        <div>
          <ResponsiveTable
            table={table}
            globalFilter={globalFilter}
            setGlobalFilter={handleGlobalFilterChange}
            loading={loading}
            hasFilter={true}
            filters={filterComponents}
            hasFooter={true}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
          />
        </div>
      </Animate>

      <CreatePolicyModal
        showModal={showCreatePolicyModal}
        setShowModal={setShowCreatePolicyModal}
        onPolicyCreated={() => {
          listPolicies(
            pagination.pageIndex + 1,
            searchQueryString,
            companyFilter,
            agencyFilter,
            statusFilter
          );
        }}
      />
    </>
  );
}
